import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, findRows, insertRow, listRows, updateRow, updateRows } from "@/lib/adminApi";
import { REVIEW_CATEGORIES, REVIEW_STATUS, metaFor, optionsFrom, type Review } from "@/lib/adminModules";
import type { Testimonial } from "@/lib/adminContent";
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnAccent,
  btnGhost,
  btnPrimary,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";
import { useAssetUrlMap } from "@/hooks/useAssetUrlMap";
import { isStoragePath } from "@/lib/mediaStorage";

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-accent-500" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <i key={star} className={star <= rating ? "ri-star-fill" : "ri-star-line text-foreground-300"}></i>
      ))}
    </span>
  );
}

export default function AdminReviews() {
  const [rows, setRows] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [featuring, setFeaturing] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Review>("reviews", "visit_date"));
      window.dispatchEvent(new Event("admin-badges-refresh"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load reviews.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Review>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("reviews", id, patch);
      if (message) notify(message);
      window.dispatchEvent(new Event("admin-badges-refresh"));
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const remove = async (id: number) => {
    try {
      await deleteRow("reviews", id);
      setRows((list) => list.filter((row) => row.id !== id));
      setSelectedId(null);
      notify("Review deleted.");
      window.dispatchEvent(new Event("admin-badges-refresh"));
    } catch {
      setError("We couldn't delete that review. Please try again.");
    }
  };

  /** Publish a review AND promote it to a featured homepage testimonial in one click. */
  const featureOnHomepage = async (review: Review) => {
    setFeaturing(true);
    try {
      await updateRow("reviews", review.id, { status: "published" });
      const payload = {
        name: review.visitor_name,
        role: review.role || null,
        location: review.location || null,
        quote: review.comment || "",
        experience: review.experience || null,
        rating: review.rating,
        avatar: review.avatar || null,
        experience_slug: review.experience_slug || null,
        featured: true,
        sort_order: 0,
        status: "published",
      };
      // Idempotent: if this review was already promoted (matched by the same
      // guest name + quote), refresh that testimonial instead of adding a copy.
      const existing = await findRows<Testimonial>("testimonials", "name", review.visitor_name);
      const match = existing.find((item) => (item.quote || "") === (review.comment || ""));
      if (match) {
        await updateRow("testimonials", match.id, payload);
        notify("Updated the existing homepage testimonial.");
      } else {
        await insertRow("testimonials", payload);
        notify("Approved and featured on the homepage.");
      }
      setRows((list) =>
        list.map((row) => (row.id === review.id ? { ...row, status: "published" } : row)),
      );
      window.dispatchEvent(new Event("admin-badges-refresh"));
    } catch {
      setError("We couldn't feature that review. Please try again.");
    } finally {
      setFeaturing(false);
    }
  };

  const pending = useMemo(() => rows.filter((row) => row.status === "pending"), [rows]);

  const approveAll = async () => {
    const ids = pending.map((row) => row.id);
    if (ids.length === 0) return;
    const snapshot = rows;
    setApprovingAll(true);
    setRows((list) => list.map((row) => (ids.includes(row.id) ? { ...row, status: "published" } : row)));
    try {
      await updateRows("reviews", ids, { status: "published" });
      notify(`${ids.length} review${ids.length === 1 ? "" : "s"} published.`);
      window.dispatchEvent(new Event("admin-badges-refresh"));
    } catch {
      setRows(snapshot);
      setError("We couldn't approve those reviews. Please try again.");
    } finally {
      setApprovingAll(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (ratingFilter !== "all" && String(row.rating) !== ratingFilter) return false;
      if (!query) return true;
      return [row.visitor_name, row.farm_name || "", row.experience || "", row.comment || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter, ratingFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const selectedPhotoPaths = useMemo(
    () => (selected?.photos || []).map((photo) => photo.src),
    [selected],
  );
  const photoUrlMap = useAssetUrlMap(selectedPhotoPaths);

  const avg = rows.length
    ? (rows.reduce((sum, row) => sum + Number(row.rating || 0), 0) / rows.length).toFixed(1)
    : "0.0";

  const columns: Column<Review>[] = [
    {
      key: "visitor",
      header: "Visitor",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.visitor_name}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.farm_name || "—"}</p>
        </div>
      ),
    },
    {
      key: "experience",
      header: "Experience",
      render: (row) => <span className="text-sm text-foreground-700">{row.experience || "—"}</span>,
    },
    { key: "rating", header: "Rating", render: (row) => <Stars rating={row.rating} /> },
    {
      key: "photos",
      header: "Photos",
      render: (row) =>
        row.photos && row.photos.length > 0 ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap">
            <i className="ri-image-2-line"></i>
            {row.photos.length}
          </span>
        ) : (
          <span className="text-xs text-foreground-400">—</span>
        ),
    },
    {
      key: "date",
      header: "Visit date",
      render: (row) => <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.visit_date)}</span>,
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(REVIEW_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Customers"
        title="Reviews"
        subtitle="Every visitor review across the platform — approve, hide or remove to keep your reputation spotless."
      />

      {pending.length > 0 ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-accent-100 border border-accent-300 rounded-lg px-4 py-3.5 mb-5">
          <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 shrink-0">
            <i className="ri-notification-3-line text-xl"></i>
          </span>
          <p className="flex-1 text-sm text-accent-900">
            <strong className="font-semibold">{pending.length}</strong>{" "}
            review{pending.length === 1 ? "" : "s"} awaiting your approval.
          </p>
          <button
            type="button"
            onClick={approveAll}
            disabled={approvingAll}
            className="inline-flex items-center gap-2 bg-accent-500 text-accent-950 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer self-start disabled:opacity-60"
          >
            <i className={approvingAll ? "ri-loader-4-line animate-spin" : "ri-check-double-line"}></i>
            Approve all
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-primary-100 border border-primary-300 rounded-lg px-4 py-3 mb-5">
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0">
            <i className="ri-checkbox-circle-line"></i>
          </span>
          <p className="text-sm text-primary-700">All reviews are approved — nothing waiting on you.</p>
        </div>
      )}

      <StatCards
        stats={[
          { label: "Total reviews", value: rows.length, icon: "ri-star-line", tone: "primary" },
          {
            label: "Published",
            value: rows.filter((row) => row.status === "published").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "Pending approval",
            value: pending.length,
            icon: "ri-time-line",
            tone: "accent",
          },
          { label: "Average rating", value: avg, icon: "ri-star-smile-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by visitor, farm, experience…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...optionsFrom(REVIEW_STATUS)]}
          />
          <Select
            label="Rating"
            value={ratingFilter}
            onChange={setRatingFilter}
            options={[
              { value: "all", label: "All ratings" },
              ...[5, 4, 3, 2, 1].map((value) => ({ value: String(value), label: `${value} stars` })),
            ]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} reviews
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading reviews…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-star-line"
            title={rows.length === 0 ? "No reviews yet" : "No reviews match"}
            message="Visitor reviews will appear here as guests return from their visits."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.visitor_name || "Review"}
        subtitle={selected?.farm_name || "Review"}
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => featureOnHomepage(selected)}
                disabled={featuring}
                className="inline-flex items-center gap-2 bg-accent-500 text-accent-950 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                <i className={featuring ? "ri-loader-4-line animate-spin" : "ri-star-fill"}></i>
                Approve &amp; feature on homepage
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { status: "published" }, "Review published.")}
                className={btnPrimary}
              >
                <i className="ri-check-line"></i> Approve
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { status: "hidden" }, "Review hidden.")}
                className={btnGhost}
              >
                <i className="ri-eye-off-line"></i> Hide
              </button>
              <button type="button" onClick={() => remove(selected.id)} className={btnAccent}>
                <i className="ri-delete-bin-line"></i> Delete
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Stars rating={selected.rating} />
              <StatusPill {...metaFor(REVIEW_STATUS, selected.status)} />
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-1">
                {selected.experience || "Experience"} · {formatDate(selected.visit_date)}
              </p>
              <p className="bg-background-100 border border-background-200 rounded-md p-4 text-sm text-foreground-800 leading-relaxed">
                {selected.comment || "No comment provided."}
              </p>
            </div>

            {selected.photos && selected.photos.length > 0 ? (
              <Field label={`Visit photos (${selected.photos.length})`}>
                <div className="grid grid-cols-3 gap-3">
                  {selected.photos.map((photo, index) => {
                    const url = isStoragePath(photo.src) ? photoUrlMap[photo.src] : photo.src;
                    return (
                      <a
                        key={`${photo.src}-${index}`}
                        href={url || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square rounded-md overflow-hidden bg-background-100 border border-background-200"
                      >
                        {url ? (
                          <img
                            src={url}
                            alt={photo.alt || `Visit photo ${index + 1}`}
                            className="w-full h-full object-cover object-top"
                          />
                        ) : (
                          <span className="w-full h-full flex items-center justify-center text-foreground-400">
                            <i className="ri-image-line text-xl"></i>
                          </span>
                        )}
                      </a>
                    );
                  })}
                </div>
              </Field>
            ) : null}

            <Field label="Rating breakdown">
              <div className="space-y-2.5">
                {REVIEW_CATEGORIES.map((cat) => {
                  const value = Number((selected.categories || {})[cat.key] || 0);
                  return (
                    <div key={cat.key} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 text-sm text-foreground-700">{cat.label}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-background-200 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-accent-500"
                          style={{ width: `${(value / 5) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-semibold text-foreground-700 w-6 text-right">{value}</span>
                    </div>
                  );
                })}
              </div>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Status changes save instantly.
      </p>
      {node}
    </div>
  );
}